import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp } from '@/lib/telegram';
import { Card, CardContent } from '@/components/ui/card';
import { Crown, TrendingUp, Bot, Wallet } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AccountSwitcherCard } from '@/components/AccountSwitcherCard';

// skeleton-ok: развилка, а не экран. Пока список ботов едет, неизвестно даже что
// показывать — онбординг или редирект на дашборд; скелетон изобразил бы один из них
// и в половине случаев соврал.
export const Route = createFileRoute('/')({ component: Landing });

function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // У партнёра уже есть боты → ведём сразу в дашборд. Иначе показываем landing.
  // Polling 8s — для свежесозданных через managed-bot deeplink ботов
  // (опрашиваем ТОЛЬКО когда список пустой, иначе грузим лишний роутер CPU).
  // Кэш прогрет в main.tsx — первый рендер без скелетона.
  const { data: bots, isPending, isError } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    refetchInterval: (q) => {
      const data = q.state.data as Array<unknown> | undefined;
      return data && data.length > 0 ? false : 8_000;
    },
  });

  // Кабинеты, где человек админ. Приглашённый в команду партнёр попадает СЮДА: своя
  // строка в `partners` у него есть всегда (tg_user_id уникален), а доступ к чужому
  // кабинету это отдельная запись. Без обработки он видел «Ботов пока нет» и кнопку
  // «Создайте бота» — при том, что позвали его работать в чужом кабинете.
  const { data: partner } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const accounts = partner?.accounts ?? [];
  const adminAccounts = accounts.filter((a) => a.role === 'admin');
  const noBots = !isPending && !isError && bots?.length === 0;
  // Переключаем только из СВОЕГО кабинета: иначе после переключения (когда активен уже
  // чужой) эффект сработал бы снова и закрутил петлю.
  const canAutoSwitch = noBots && !partner?.isDelegate && adminAccounts.length === 1;
  const switching = useRef(false);

  useEffect(() => {
    if (!canAutoSwitch || switching.current) return;
    switching.current = true;
    // Ботов нет, а админский кабинет ровно один — открываем его сразу. Выбирать не из
    // чего, а лишний экран между человеком и его работой не нужен.
    void api
      .switchContext(adminAccounts[0].partner_id)
      .then(() => {
        qc.clear();
        navigate({ to: '/dashboard' });
      })
      .catch(() => {
        switching.current = false; // не вышло — остаёмся на лендинге, он покажет выбор
      });
  }, [canAutoSwitch, adminAccounts, qc, navigate]);

  useEffect(() => {
    if (isPending || isError) return;
    if (bots && bots.length > 0) {
      navigate({ to: '/dashboard' });
      return;
    }
    // Пока идёт автопереключение или человеку есть из чего выбрать, кнопка «создать
    // бота» была бы единственным выходом с экрана и уводила бы не туда.
    if (canAutoSwitch || adminAccounts.length > 1) return;
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    tg.MainButton.setText(t('Start now').toUpperCase());
    tg.MainButton.show();
    const onClick = () => navigate({ to: '/onboarding/bot' });
    tg.MainButton.onClick(onClick);
    return () => {
      tg.MainButton.offClick(onClick);
      tg.MainButton.hide();
    };
  }, [t, navigate, bots, isPending, isError, canAutoSwitch, adminAccounts.length]);

  // Пока bots-запрос идёт ИЛИ упал (auth-сбой/сеть) — нейтральный спиннер, чтобы НЕ
  // показывать «Стать партнёром»/онбординг уже зарегистрированному партнёру (его бот
  // в БД цел, просто запрос не прошёл). При auth-ошибке __root покажет экран повтора.
  if (isPending || isError || (bots && bots.length > 0) || canAutoSwitch) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-white/30 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Своих ботов нет, но есть несколько кабинетов, где человек админ. Выбор за ним:
  // переключатель живёт в профиле, а до профиля с этого экрана не дойти (нижняя
  // навигация на «/» скрыта), поэтому показываем его прямо здесь.
  if (adminAccounts.length > 1) {
    return (
      <div className="p-6 space-y-5 pb-24">
        <div className="text-center space-y-2 mt-6">
          <h1 className="text-2xl font-bold tracking-tight">Выберите кабинет</h1>
          <p className="text-muted-foreground text-sm">
            Вас добавили в команду. Откройте кабинет, в котором будете работать.
          </p>
        </div>
        <AccountSwitcherCard />
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col items-center text-center space-y-8 pb-24">
      <div className="mt-6 relative w-full flex flex-col items-center">
        <div className="w-24 h-24 bg-card border border-white/10 rounded-3xl flex items-center justify-center mx-auto mb-6 relative z-10 shadow-xl">
          <Crown className="w-12 h-12 text-foreground" />
        </div>
        <h1 className="text-4xl font-bold tracking-tight mb-3">{t('Become a partner')}</h1>
        <p className="text-muted-foreground">{t('Hero copy')}</p>
      </div>

      <div className="flex flex-col space-y-4 w-full">
        <FeatureCard icon={<Bot />} text={t('Bullet 1')} />
        <FeatureCard icon={<TrendingUp />} text={t('Bullet 2')} />
        <FeatureCard icon={<Wallet />} text={t('Bullet 3')} />
      </div>
    </div>
  );
}

function FeatureCard({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Card className="bg-card border-white/5">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="text-foreground">{icon}</div>
        <span className="font-semibold">{text}</span>
      </CardContent>
    </Card>
  );
}
